import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Injectable } from "@angular/core";
import {
  Auth,
  FacebookAuthProvider,
  GoogleAuthProvider,
  OAuthProvider,
  UserCredential,
  applyActionCode,
  checkActionCode,
  confirmPasswordReset,
  getRedirectResult,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  verifyPasswordResetCode,
} from "@angular/fire/auth";
import { environment } from "@env";
import type {
  AuthData,
  AuthProvider,
  AuthResponse,
  AuthenticatedUserRegisterData,
  NewUser,
  RegisterResponse,
  User,
} from "@models";
import { BehaviorSubject, Observable, from, of } from "rxjs";
import { map } from "rxjs/operators";
import type { LegacyResponse } from "./response.model";
import { CookieService } from "ngx-cookie-service";
import { clearCurrentUser } from "@store";
import { Store } from "@ngrx/store";

@Injectable({ providedIn: "root" })
export class AuthService {
  public isLoggingInSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public isLoggingIn$: Observable<boolean> = this.isLoggingInSubject.asObservable();
  private _usersApiUrl: string = environment.usersApiUrl;

  constructor(
    private _httpClient: HttpClient,
    private _firebaseAuth: Auth,
    private _cookiesService: CookieService,
    private _store: Store,
  ) {}

  private _getAuthProvider(
    authProvider: AuthProvider,
  ): GoogleAuthProvider | FacebookAuthProvider | OAuthProvider {
    switch (authProvider) {
      case "GOOGLE":
        return new GoogleAuthProvider();
      case "FACEBOOK":
        return new FacebookAuthProvider();
      case "APPLE":
        return new OAuthProvider("apple.com");
      default:
        throw new Error(`Unsupported auth provider: ${authProvider}`);
    }
  }

  public login(data: AuthData): Observable<AuthResponse> {
    return this._httpClient.post<AuthResponse>(`${this._usersApiUrl}/login`, data);
  }

  public registerWithEmailAndPassword(
    newUser: NewUser,
  ): Observable<LegacyResponse<RegisterResponse>> {
    let headers: HttpHeaders = new HttpHeaders();
    headers = headers.set("FBC", this._cookiesService.get("_fbc") || "");
    headers = headers.set("FBP", this._cookiesService.get("_fbp") || "");

    return this._httpClient.post<LegacyResponse<RegisterResponse>>(
      `${this._usersApiUrl}/add`,
      newUser,
      { headers },
    );
  }

  public registerAuthenticatedUser(newUser: AuthenticatedUserRegisterData): Observable<User> {
    return this._httpClient.post<User>(`${this._usersApiUrl}/add-authenticated`, newUser);
  }

  public forgotPassword(payload: { userEmail: string }): Observable<LegacyResponse<null>> {
    return this._httpClient.put<LegacyResponse<null>>(
      `${this._usersApiUrl}/forgot-password`,
      payload,
    );
  }

  public getUserByEmail(userEmail: string): Observable<LegacyResponse<RegisterResponse>> {
    return this._httpClient.post<LegacyResponse<RegisterResponse>>(`${this._usersApiUrl}/search`, {
      userEmail,
    });
  }

  public getUserByUsername(userName: string): Observable<LegacyResponse<RegisterResponse>> {
    return this._httpClient.post<LegacyResponse<RegisterResponse>>(`${this._usersApiUrl}/search`, {
      userName,
    });
  }

  public getIdToken(): Observable<string | null> {
    return new Observable<string | null>((observer) => {
      return this._firebaseAuth.onAuthStateChanged((user) => {
        if (!user) return observer.next(null);

        return user.getIdToken().then((token) => {
          observer.next(token);
        });
      });
    });
  }

  public loginWithEmailAndPassword(email: string, password: string): Observable<UserCredential> {
    return from(signInWithEmailAndPassword(this._firebaseAuth, email, password));
  }

  public loginWithAuthProvider(authProvider: AuthProvider): Observable<UserCredential> {
    const provider: GoogleAuthProvider | FacebookAuthProvider | OAuthProvider =
      this._getAuthProvider(authProvider);

    provider.addScope("email");

    return from(signInWithPopup(this._firebaseAuth, provider));
  }

  public getDataAfterAuthProviderRedirect(): Observable<UserCredential | null> {
    return from(getRedirectResult(this._firebaseAuth));
  }

  public isAuthenticated(): Observable<boolean> {
    return new Observable<boolean>((observer) => {
      return this._firebaseAuth.onAuthStateChanged((user) => {
        observer.next(!!user);
      });
    });
  }

  public getEmailFromOobCode(oobCode: string): Observable<string | null> {
    return from(checkActionCode(this._firebaseAuth, oobCode)).pipe(
      map((info) => info.data.email ?? null),
    );
  }

  public applyEmailVerification(oobCode: string): Observable<void> {
    return from(applyActionCode(this._firebaseAuth, oobCode));
  }

  public verifyPasswordResetCode(oobCode: string): Observable<string> {
    return from(verifyPasswordResetCode(this._firebaseAuth, oobCode));
  }

  public resetPassword(newPassword: string, oobCode: string): Observable<void> {
    return from(confirmPasswordReset(this._firebaseAuth, oobCode, newPassword));
  }

  public forceRefreshToken(): Observable<string | null> {
    const currentUser = this._firebaseAuth.currentUser;
    if (!currentUser) return of(null);
    return from(currentUser.getIdToken(true));
  }

  public logout(): Observable<void> {
    this._store.dispatch(clearCurrentUser());
    return from(signOut(this._firebaseAuth));
  }
}
